import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { TripRequest } from '@marageti/z4-sdk/lib/travel';
import { PecvDecisionContributor, PecvInferenceResult } from './pecv-types';
import {
  formatPecvContributorValue,
  formatPecvCurrency,
  formatPecvPercent,
  getPecvFeatureLabel,
  getPecvResultData,
  getRankedPecvContributors,
  getTripRequestContributorValue,
} from './pecv-utils';

const PecvContributorRow = ({
  contributor,
  bgcolor,
  tripRequest,
}: {
  contributor: PecvDecisionContributor,
  bgcolor: string,
  tripRequest?: TripRequest,
}) => {
  const rawValue = getTripRequestContributorValue(contributor.feature, tripRequest) ?? contributor.value;
  const formattedValue = formatPecvContributorValue(contributor.feature, rawValue);

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      spacing={1.5}
      sx={{
        px: 1.25,
        py: 0.75,
        borderRadius: 1,
        bgcolor,
      }}
    >
      <Typography variant="body">
        {getPecvFeatureLabel(contributor.feature)}
      </Typography>
      {formattedValue != null && (
        <Typography variant="bodyBold" sx={{ whiteSpace: 'nowrap' }}>
          {formattedValue}
        </Typography>
      )}
    </Stack>
  );
};

type PecvIndicatorViewProps = {
  canViewInferenceDetails: boolean;
  errorMessage: string | null;
  inferenceResult: PecvInferenceResult | null;
  initialPecv: number;
  isLoading: boolean;
  open: boolean;
  tripRequest: TripRequest;
  onClose: () => void;
  onOpen: (event: React.MouseEvent<HTMLButtonElement>) => void;
};

const PecvIndicatorView = ({
  canViewInferenceDetails,
  errorMessage,
  inferenceResult,
  initialPecv,
  isLoading,
  open,
  tripRequest,
  onClose,
  onOpen,
}: PecvIndicatorViewProps) => {
  const formatted = formatPecvCurrency(initialPecv);

  const pecvChipSx = {
    px: 1,
    py: 0.25,
    border: 0,
    display: 'inline-flex',
    alignItems: 'center',
    bgcolor: 'info.light',
    borderRadius: '6.25rem',
  };

  if (!canViewInferenceDetails) {
    return (
      <Tooltip title={`Estimated Value: ${formatted}`} placement="top">
        <Box sx={pecvChipSx}>
          <Typography variant="captionBold" color="info.dark">
            {formatted}
          </Typography>
        </Box>
      </Tooltip>
    );
  }

  const resultsData = getPecvResultData(inferenceResult);
  const contributors = (
    resultsData.decisionContributors
    || resultsData.Decision_Contributors
    || []
  ).slice(0, 10);
  const goodMatchIndicators = getRankedPecvContributors(contributors, 'Positive');
  const redFlags = getRankedPecvContributors(contributors, 'Negative');

  const stopPropagation = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  return (
    <>
      <Tooltip title="View PECV inference details" placement="top">
        <Box
          component="button"
          type="button"
          onClick={onOpen}
          sx={{
            ...pecvChipSx,
            cursor: 'pointer',
            '&:hover': {
              bgcolor: 'info.main',
              '& .MuiTypography-root': {
                color: 'common.white',
              },
            },
          }}
        >
          <Typography variant="captionBold" color="info.dark">
            {formatted}
          </Typography>
        </Box>
      </Tooltip>
      <Dialog
        open={open}
        onClose={onClose}
        onClick={stopPropagation}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>PECV Inference Details</DialogTitle>
        <DialogContent sx={{ pt: 0.5 }}>
          {isLoading && (
            <Stack alignItems="center" py={4}>
              <CircularProgress size={28} />
            </Stack>
          )}
          {!isLoading && errorMessage && (
            <Alert severity="info">{errorMessage}</Alert>
          )}
          {!isLoading && !errorMessage && (
            <Stack spacing={2.5}>
              <Stack direction="row" spacing={2} justifyContent="space-between" flexWrap="wrap">
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">Estimated value</Typography>
                  <Typography variant="h4">{formatPecvCurrency(resultsData.value ?? initialPecv)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">Recommendation</Typography>
                  <Typography variant="bodyBold">{resultsData.matchDecision || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">Confidence</Typography>
                  <Typography variant="bodyBold">{formatPecvPercent(resultsData.confidence)}</Typography>
                </Box>
              </Stack>

              <Stack spacing={0.5}>
                <Typography variant="bodyBold">What influenced this result</Typography>
                <Typography variant="caption" color="text.secondary">
                  These are the strongest signals the model used, grouped by whether they helped or hurt the match estimate.
                </Typography>
              </Stack>

              {contributors.length === 0 ? (
                <Alert severity="info">
                  No decision contributors were stored for this result.
                </Alert>
              ) : (
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  alignItems="flex-start"
                >
                  <Box flex={1} width="100%">
                    <Typography variant="bodyBold" color="success.dark">
                      Good Match Indicators
                    </Typography>
                    {goodMatchIndicators.length === 0 ? (
                      <Typography variant="caption" color="text.secondary" display="block" mt={0.75}>
                        No strong positive indicators were returned.
                      </Typography>
                    ) : (
                      <Stack spacing={1} mt={1}>
                        {goodMatchIndicators.map((contributor) => (
                          <PecvContributorRow
                            key={`${contributor.feature}-${contributor.impact}`}
                            contributor={contributor}
                            bgcolor="success.light"
                            tripRequest={tripRequest}
                          />
                        ))}
                      </Stack>
                    )}
                  </Box>

                  <Box flex={1} width="100%">
                    <Typography variant="bodyBold" color="warning.dark">
                      Red Flags
                    </Typography>
                    {redFlags.length === 0 ? (
                      <Typography variant="caption" color="text.secondary" display="block" mt={0.75}>
                        No strong negative indicators were returned.
                      </Typography>
                    ) : (
                      <Stack spacing={1} mt={1}>
                        {redFlags.map((contributor) => (
                          <PecvContributorRow
                            key={`${contributor.feature}-${contributor.impact}`}
                            contributor={contributor}
                            bgcolor="warning.light"
                            tripRequest={tripRequest}
                          />
                        ))}
                      </Stack>
                    )}
                  </Box>
                </Stack>
              )}

              <Stack spacing={0.5}>
                {resultsData.checkpoint && (
                  <Typography variant="caption" color="text.secondary">
                    Checkpoint:
                    {' '}
                    {resultsData.checkpoint}
                  </Typography>
                )}
                {inferenceResult?.modelVersion && (
                  <Typography variant="caption" color="text.secondary">
                    Model version:
                    {' '}
                    {inferenceResult.modelVersion}
                  </Typography>
                )}
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PecvIndicatorView;
